// Driver API client for fetching race data, attaches JWT from localStorage

import { DriverRaceDataResponse } from '../models/DriverRaceDataResponse';
import { MatchDriverResponse } from '../models/MatchDriverResponse';
import { PagedResponse } from '../models/PagedResponse';
import { FindDriverRequestBody } from '../models/requestBodies/FindDriverRequestBody';
import { type Membership } from '../models/Membership';
import { DriverUsedFacility } from '../models/RaceData';
import { FilterCommonResponse } from '../models/FilterCommonResponse';
import { CareerProgress } from '../models/CareerProgress';
import { LeaderboardWithResults } from '../models/LeaderboardWithResults';
import { LeaderboardWithRecentResults } from '../models/LeaderboardWithRecentResults';
import { RaceAroundWorldProgress } from '../models/RaceAroundWorldProgress';
import { BaseClient } from './BaseClient';

export interface GetUserRacesParams {
  take: number;
  skip: number;
  car?: string;
  track?: string;
}

export interface GetFitleredUserRacesParams {
  take: number;
  skip: number;
  car: string;
  track: string;
  trackLayout: string;
}

const getDriverApiBaseUrl = (): string => {
  const explicit = process.env.REACT_APP_WORLD_API_BASE_URL;
  if (!explicit) {
    throw new Error('Missing REACT_APP_WORLD_API_BASE_URL environment variable');
  }
  return explicit.endsWith('/') ? explicit : explicit + '/';
};

class DriverClient extends BaseClient {
  private baseUrl: string;

  constructor() {
    super();
    this.baseUrl = getDriverApiBaseUrl();
  }

  // GET {base}/racedata/user/races?Take=..&Skip=..&DriverId=..
  public async getUserRaces(params: GetUserRacesParams): Promise<PagedResponse<DriverRaceDataResponse>> {
    const { take, skip, car, track } = params;
    const search = new URLSearchParams({
      Take: String(take),
      Skip: String(skip),
    });

    if (car) {
      search.append('Car', car);
    }
    if (track) {
      search.append('Track', track);
    }

    const url = `${this.baseUrl}racedata/user/races?${search.toString()}`;

    const response = await this.fetchWithAuth(url, { method: 'GET' });
    if (!response.ok) {
      throw new Error(`Failed to load user races (${response.status})`);
    }
    const payload = (await response.json()) as PagedResponse<DriverRaceDataResponse> | DriverRaceDataResponse[];

    if (Array.isArray(payload)) {
      return { data: payload, totalCount: payload.length };
    }
    return payload;
  }

  public async getFilterCars(track: string | undefined): Promise<FilterCommonResponse[]> {
    let url = `${this.baseUrl}racedata/user/filter/cars`;

    if (track) {
      url += `?withTrack=${track}`;
    }

    const response = await this.fetchWithAuth(url, { method: 'GET' });
    if (!response.ok) {
      throw new Error(`Failed to load car filters (${response.status})`);
    }
    return await response.json();
  }

  public async getFilterTracks(car: string | undefined): Promise<FilterCommonResponse[]> {
    let url = `${this.baseUrl}racedata/user/filter/tracks`;
    if (car) {
      url += `?withCar=${car}`;
    }

    const response = await this.fetchWithAuth(url, { method: 'GET' });
    if (!response.ok) {
      throw new Error(`Failed to load track filters (${response.status})`);
    }
    return await response.json();
  }

  public async findDriverByUserData(userData: FindDriverRequestBody): Promise<MatchDriverResponse | null> {
    const search = new URLSearchParams({
      email: userData.email,
      firstName: userData.firstName,
      lastName: userData.lastName
    });
    const url = `${this.baseUrl}driver/find?${search.toString()}`;

    const response = await this.fetchWithAuth(url, { method: 'GET' });

    if (!response.ok) {
      throw new Error(`Failed to find driver by user data (${response.status})`);
    }

    const text = await response.text();
    if (!text) {
      return null;
    }

    return (JSON.parse(text)) as MatchDriverResponse | null;
  }

  // GET {base}/membership/{driverId}
  public async getMembershipByDriverId(driverId: string): Promise<Membership | null> {
    if (!driverId) {
      throw new Error('driverId is required');
    }

    const url = `${this.baseUrl}driver/membership`;

    const response = await this.fetchWithAuth(url, { method: 'GET' });
    if (response.status === 404) {
      return null;
    }
    if (!response.ok) {
      throw new Error(`Failed to load membership (${response.status})`);
    }
    return (await response.json()) as Membership | null;
  }

  public async getDriverUsedFacilities(driverId: string): Promise<DriverUsedFacility[] | null> {
    if (!driverId) {
      throw new Error('driverId is required');
    }

    const url = `${this.baseUrl}racedata/facilities`;

    const response = await this.fetchWithAuth(url, { method: 'GET' });
    if (response.status === 404) {
      return null;
    }
    if (!response.ok) {
      throw new Error(`Failed to load used facilities (${response.status})`);
    }
    return (await response.json()) as DriverUsedFacility[] | null;
  }

  public async getDriverLeaderboard(params: GetFitleredUserRacesParams): Promise<DriverRaceDataResponse[] | null> {
    const { take, skip, car, track, trackLayout } = params;
    const search = new URLSearchParams({
      Take: String(take),
      Skip: String(skip),
      Car: String(car),
      Track: String(track),
      TrackLayout: String(trackLayout)
    });

    const url = `${this.baseUrl}racedata/leaderboard?${search.toString()}`;

    const response = await this.fetchWithAuth(url, { method: 'GET' });
    if (!response.ok) {
      throw new Error(`Failed to load user races (${response.status})`);
    }
    const payload = (await response.json()) as DriverRaceDataResponse[] | null;

    return payload;
  }

  public async getLeaderboardById(id: string, days?: string | null, count?: string | null): Promise<LeaderboardWithResults> {
    const search = new URLSearchParams();
    if (days) search.append('days', days);
    if (count) search.append('count', count);
    const query = search.toString();

    const url = `${this.baseUrl}racedata/top/boards/${id}${query ? `?${query}` : ''}`;

    const response = await this.fetchWithAuth(url, { method: 'GET' });
    if (!response.ok) {
      throw new Error(`Failed to load leaderboard ${id} (${response.status})`);
    }
    return (await response.json()) as LeaderboardWithResults;
  }

  public async getRecentResultsById(id: string, count?: string | null): Promise<LeaderboardWithRecentResults> {
    const search = new URLSearchParams();
    if (count) search.append('count', count);
    const query = search.toString();

    const url = `${this.baseUrl}racedata/latest/boards/${id}${query ? `?${query}` : ''}`;

    const response = await this.fetchWithAuth(url, { method: 'GET' });
    if (!response.ok) {
      throw new Error(`Failed to load recent results ${id} (${response.status})`);
    }
    return (await response.json()) as LeaderboardWithRecentResults;
  }

  public async getDriverCareerProgress(driverId: string): Promise<CareerProgress[] | null> {
    if (!driverId) {
      throw new Error('driverId is required');
    }

    const url = `${this.baseUrl}racedata/career`;

    const response = await this.fetchWithAuth(url, { method: 'GET' });
    if (response.status === 404) {
      return null;
    }
    if (!response.ok) {
      throw new Error(`Failed to load career progress (${response.status})`);
    }
    return (await response.json()) as CareerProgress[] | null;
  }

  public async getRaceAroundWorldProgress(driverId: string): Promise<RaceAroundWorldProgress[] | null> {
    if (!driverId) {
      throw new Error('driverId is required');
    }

    const url = `${this.baseUrl}driver/around-the-world`;

    const response = await this.fetchWithAuth(url, { method: 'GET' });
    if (response.status === 404) {
      return null;
    }
    if (!response.ok) {
      throw new Error(`Failed to load Race Around the World progress (${response.status})`);
    }
    return (await response.json()) as RaceAroundWorldProgress[] | null;
  }

  //bookings section

  public async getBookingLocations(): Promise<string[]> {
    const url = `${this.baseUrl}locations`;
    const response = await this.fetchWithAuth(url, { method: 'GET' });
    if (!response.ok) {
      throw new Error(`Failed to load available locations (${response.status})`);
    }
    return (await response.json()) as string[];
  }
}


const driverApi = new DriverClient();
export default driverApi;


